"use client";
import { Flex } from "@/components/Flex";
import KaiChatFloatingIcon from "@/features/AI/KaiChat/components/KaiChatFloatingIcon";
import { useChatEditorCommunicator } from "@/features/AI/KaiChat/hooks/useChatEditorCommunicator";
import KaiChatInterface from "@/features/AI/KaiChat/KaiChatInterface";
import { ChatSuggestionOpt } from "@/features/AI/KaiChat/types";
import { borderRadius } from "@/utils/borderRadius";
import { htmlToMd } from "@knowt/editor/md/parsing/parse";
import { FlashcardSet, ItemType, Note } from "@knowt/syncing/graphql/schema";
import { CommonReadOnlyChatPrompts } from "@knowt/syncing/hooks/ai/constants";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import Grow from "@mui/material/Grow";
import { SparkleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import Mixpanel from "@/utils/analytics/Mixpanel";

type Props = {
    floatStyles?: React.CSSProperties;
} & ({ note: Note; flashcardSet?: never } | { flashcardSet: FlashcardSet; note?: never });

const READONLY_KAI_CHAT_ID = "readonly-kai-chat";

export const clickReadonlyKaiChat = () => {
    document.getElementById(READONLY_KAI_CHAT_ID)?.click();
};

export const ReadonlyChatInterface = ({ note, flashcardSet, floatStyles }: Props) => {
    const { userId } = useCurrentUser();
    const [isChatOpen, setIsChatOpen] = useState(false);
    const { canShowAds } = useCurrentUser();

    const setFullContent = useChatEditorCommunicator(s => s.setFullContent);
    const processChat = useChatEditorCommunicator(s => s.processChat);
    const fullContent = useChatEditorCommunicator(s => s.fullContent);

    const isOwner = userId === note?.userId || userId === flashcardSet?.userId;

    useEffect(() => {
        if (note?.content && !fullContent) setFullContent(note?.content);

        if (flashcardSet?.flashcards && !fullContent)
            setFullContent(
                JSON.stringify(
                    flashcardSet.flashcards.map(f => ({
                        term: htmlToMd(f?.term || ""),
                        definition: htmlToMd(f?.definition || ""),
                    }))
                )
            );
    }, [note?.content, flashcardSet?.flashcards, fullContent, setFullContent]);

    const basicSuggestions: ChatSuggestionOpt[] = [
        {
            label: "What are they key terms I should focus on?",
            icon: <SparkleIcon size={20} />,
            onClick: async () => {
                await processChat({
                    prompt: CommonReadOnlyChatPrompts.WHAT_ARE_THE_KEY_TERMS,
                    inputQuestion: "What are the key terms I should focus on?",
                });
            },
        },
        {
            label: "Give me a very brief summary of what is covered in this file",
            icon: <SparkleIcon size={20} />,
            onClick: async () => {
                await processChat({
                    prompt: CommonReadOnlyChatPrompts.BRIEF_SUMMARY,
                    inputQuestion: "Give me a very brief summary of what is covered in this file",
                });
            },
        },
    ];

    return (
        <Flex
            style={{
                position: "sticky",
                right: "7rem",
                bottom: canShowAds ? "100px" : "3rem",
                flexDirection: "row-reverse",
                zIndex: 99,
                alignItems: "center",
                ...floatStyles,
            }}>
            <KaiChatFloatingIcon
                id={READONLY_KAI_CHAT_ID}
                onClick={async () => {
                    setIsChatOpen(!isChatOpen);
                    if (!isOwner && !isChatOpen) {
                        Mixpanel.track("Ask Kai - Opened", {
                            type: note ? ItemType.NOTE : ItemType.FLASHCARDSET,
                            noteId: note ? note.noteId : undefined,
                            flashcardSetId: flashcardSet ? flashcardSet.flashcardSetId : undefined,
                        });
                    }
                }}
                isOpen={isChatOpen}
            />
            <Grow in={isChatOpen} style={{ transformOrigin: "bottom right" }} unmountOnExit mountOnEnter>
                <div
                    style={{
                        position: "absolute",
                        bottom: "0",
                        right: "calc(100% + 1rem)", // move 1rem to the left of floating icon
                        width: "65rem",
                        height: "65rem",
                        maxHeight: "60rem",
                        maxWidth: "60rem",
                        boxShadow: "0px 2px 15px 0px var(--color-shadow)",
                        borderRadius: borderRadius.card,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                    }}>
                    <KaiChatInterface
                        placeholder="Ask Kai any questions you have!"
                        suggestionOptions={{
                            basic: basicSuggestions,
                            onSelected: [],
                            uponAnswerGenerated: [],
                        }}
                        containerStyle={{ width: "100%", height: "100%", minHeight: "fit-content" }}
                    />
                </div>
            </Grow>
        </Flex>
    );
};
